import React, { useState } from 'react';
import db from '../lib/instant';

const ProjectInvitationManager = ({ projectId, projectName, userRole }) => {
  const user = db.useUser();
  const [loading, setLoading] = useState(null);

  // Query invitations for this project
  const { data } = db.useQuery(projectId ? {
    invitations: {},
    users: {}
  } : {});

  // Can only manage invitations if owner or admin
  const canManageInvitations = userRole === 'owner' || userRole === 'admin';

  // Get pending invitations for this project
  const pendingInvitations = data?.invitations?.filter(
    inv => inv.project_id === projectId && inv.status === 'pending'
  ) || [];

  const handleResendInvitation = async (invitation) => {
    setLoading(invitation.id);
    
    try {
      // Update the invitation with a new expiration date
      const newExpiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      
      await db.transact([
        db.tx.invitations[invitation.id].update({
          ...invitation,
          expires_at: newExpiresAt,
          created_at: new Date().toISOString() // Update created_at to show it's been resent
        })
      ]);
    } catch (error) {
      console.error('Error resending invitation:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleCancelInvitation = async (invitation) => {
    setLoading(invitation.id);
    
    try {
      await db.transact([
        db.tx.invitations[invitation.id].update({
          ...invitation,
          status: 'cancelled'
        })
      ]);
    } catch (error) {
      console.error('Error canceling invitation:', error);
    } finally {
      setLoading(null);
    }
  };

  if (!canManageInvitations || pendingInvitations.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Pending Invitations</h3>
          <p className="text-sm text-gray-600">Manage invitations sent to join this project</p>
        </div>
        <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
          {pendingInvitations.length} pending
        </span>
      </div>
      
      <div className="space-y-4">
        {pendingInvitations.map(invitation => {
          const isExpired = new Date(invitation.expires_at) < new Date();
          const inviterUser = data?.users?.find(u => u.id === invitation.invited_by);
          
          return (
            <div key={invitation.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <p className="font-medium text-gray-900">{invitation.invited_email}</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    invitation.role === 'admin' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {invitation.role}
                  </span>
                  {isExpired && (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                      Expired
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>Invited by: {inviterUser?.email?.split('@')[0] || 'Unknown'}</span>
                  <span>•</span>
                  <span>{isExpired ? 'Expired' : `Expires: ${new Date(invitation.expires_at).toLocaleDateString()}`}</span>
                  <span>•</span>
                  <span>Sent: {new Date(invitation.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => handleResendInvitation(invitation)}
                  disabled={loading === invitation.id}
                  className="button-primary px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-all hover-lift"
                >
                  {loading === invitation.id ? (
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full spinner"></div>
                      <span>Resending...</span>
                    </div>
                  ) : (
                    'Resend'
                  )}
                </button>
                <button
                  onClick={() => handleCancelInvitation(invitation)}
                  disabled={loading === invitation.id}
                  className="button-danger px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-all hover-lift"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectInvitationManager;